import { useTranslation } from "react-i18next";

import { StatusBanner } from "../design-system/StatusBanner";
import { useTrackingRuntime } from "../features/tracking/TrackingRuntimeContext";
import { useCacheHealth } from "../offline/useCacheHealth";

export function SyncStatusBanner({ isOnline }: { isOnline: boolean }) {
  const { t } = useTranslation("foundation");
  const { availability, isMutating } = useTrackingRuntime();
  const cacheHealth = useCacheHealth();

  if (!isOnline || availability === "offline") {
    return (
      <StatusBanner
        description={t("shell.offlineDescription")}
        label={t("shell.connectionStatusLabel")}
        title={t("shell.offlineTitle")}
        tone="warning"
      />
    );
  }

  /*
   * Ranked below being offline but above every healthy state: a full cache is
   * a real degradation the reader can act on, and unlike the storage errors
   * below it does not stop anything working right now.
   */
  if (cacheHealth === "full") {
    return (
      <StatusBanner
        description={t("shell.storageFullDescription")}
        label={t("shell.connectionStatusLabel")}
        title={t("shell.storageFullTitle")}
        tone="warning"
      />
    );
  }

  if (availability === "storage-opening") {
    return (
      <StatusBanner
        description={t("shell.storageUpgradeDescription")}
        label={t("shell.connectionStatusLabel")}
        title={t("shell.storageUpgradeTitle")}
        tone="info"
      />
    );
  }

  if (availability === "storage-blocked") {
    return (
      <StatusBanner
        description={t("shell.storageBlockedDescription")}
        label={t("shell.connectionStatusLabel")}
        title={t("shell.storageBlockedTitle")}
        tone="danger"
      />
    );
  }

  if (availability === "storage-error") {
    return (
      <StatusBanner
        description={t("shell.storageErrorDescription")}
        label={t("shell.connectionStatusLabel")}
        title={t("shell.storageErrorTitle")}
        tone="danger"
      />
    );
  }

  if (availability === "reconnecting") {
    return (
      <StatusBanner
        description={t("shell.reconnectingDescription")}
        label={t("shell.connectionStatusLabel")}
        title={t("shell.reconnectingTitle")}
        tone="info"
      />
    );
  }

  if (isMutating) {
    return (
      <StatusBanner
        description={t("shell.savingDescription")}
        label={t("shell.connectionStatusLabel")}
        title={t("shell.savingTitle")}
        tone="info"
      />
    );
  }

  return null;
}
